import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Grid, CircularProgress,
  RadioGroup, FormControlLabel, Radio, FormLabel, Autocomplete, Box,
} from '@mui/material';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import { DecaissementCreate, DecaissementUpdate, MOTIF_LABELS } from '../../types/decaissement';
import { Beneficiaire } from '../../types/beneficiaire';
import { Supplier } from '../../types/supplier';
import { Projet } from '../../types/projet';
import { ModePaiement } from '../../types/modePaiement';
import { decaissementsAPI, beneficiairesAPI, suppliersAPI, projetsAPI, modesPaiementAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';
import PriceInput, { parsePriceValue } from '../../components/PriceInput';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editId?: number;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const DecaissementFormDialog: React.FC<Props> = ({ open, onClose, onSuccess, editId, minimized: controlledMinimized, onMinimizedChange }) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [fournisseurs, setFournisseurs] = useState<Supplier[]>([]);
  const [beneficiaires, setBeneficiaires] = useState<Beneficiaire[]>([]);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [modesPaiement, setModesPaiement] = useState<ModePaiement[]>([]);

  const [formData, setFormData] = useState<DecaissementCreate>({
    date: new Date().toISOString().split('T')[0],
    projet_id: null,
    beneficiaire_type: 'fournisseur',
    fournisseur_id: null,
    beneficiaire_id: null,
    motif: 'achat',
    libelle: '',
    paiement_type: 'totalite',
    montant_total: 0,
    montant_paye: 0,
    reste: 0,
    mode_paiement_id: null,
    reference_paiement: '',
    piece_justificatif: '',
    status: 'brouillon',
    date_echeance: null,
  });

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new décaissements (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: DecaissementCreate;
  }>('decaissement', !editId);

  useEffect(() => {
    if (open) {
      loadDropdownData();
      if (editId) {
        loadDecaissement();
      } else {
        const draft = loadDraft();
        if (draft) {
          setFormData(draft.formData);
        } else {
          resetForm();
        }
      }
    }
  }, [open, editId]);

  const loadDropdownData = async () => {
    try {
      const [fourRes, benRes, projRes, modeRes] = await Promise.all([
        // the /all endpoints: the paginated lists stopped at 100
        suppliersAPI.getAllForDropdown(),
        beneficiairesAPI.getAll(),
        projetsAPI.getAllForDropdown(),
        modesPaiementAPI.getAll(),
      ]);
      setFournisseurs(fourRes || []);
      setBeneficiaires(benRes);
      setProjets([...(projRes || [])].sort((a, b) => b.created_at.localeCompare(a.created_at)));
      setModesPaiement(modeRes);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      projet_id: null,
      beneficiaire_type: 'fournisseur',
      fournisseur_id: null,
      beneficiaire_id: null,
      motif: 'achat',
      libelle: '',
      paiement_type: 'totalite',
      montant_total: 0,
      montant_paye: 0,
      reste: 0,
      mode_paiement_id: null,
      reference_paiement: '',
      piece_justificatif: '',
      status: 'brouillon',
      date_echeance: null,
    });
  };

  const loadDecaissement = async () => {
    if (!editId) return;
    try {
      setLoading(true);
      const data = await decaissementsAPI.getById(editId);
      setFormData({
        date: data.date,
        projet_id: data.projet_id,
        beneficiaire_type: data.beneficiaire_type,
        fournisseur_id: data.fournisseur_id,
        beneficiaire_id: data.beneficiaire_id,
        motif: data.motif,
        libelle: data.libelle || '',
        paiement_type: data.paiement_type,
        montant_total: data.montant_total,
        montant_paye: data.montant_paye,
        reste: data.reste,
        mode_paiement_id: data.mode_paiement_id,
        reference_paiement: data.reference_paiement || '',
        piece_justificatif: data.piece_justificatif || '',
        status: data.status,
        date_echeance: data.date_echeance || null,
      });
    } catch (err) {
      showError('Erreur lors du chargement du décaissement');
    } finally {
      setLoading(false);
    }
  };

  const handlePaiementTypeChange = (type: string) => {
    if (type === 'totalite') {
      setFormData({
        ...formData,
        paiement_type: type,
        montant_paye: formData.montant_total,
        reste: 0,
      });
    } else {
      setFormData({
        ...formData,
        paiement_type: type,
        montant_paye: 0,
        reste: formData.montant_total,
      });
    }
  };

  const handleMontantTotalChange = (value: number | null | string) => {
    const numValue = parsePriceValue(value);
    if (formData.paiement_type === 'totalite') {
      setFormData({
        ...formData,
        montant_total: numValue,
        montant_paye: numValue,
        reste: 0,
      });
    } else {
      setFormData({
        ...formData,
        montant_total: numValue,
        reste: numValue - parsePriceValue(formData.montant_paye),
      });
    }
  };

  const handleMontantPayeChange = (value: number | null | string) => {
    const numValue = parsePriceValue(value);
    setFormData({
      ...formData,
      montant_paye: numValue,
      reste: parsePriceValue(formData.montant_total) - numValue,
    });
  };

  const handleSubmit = async () => {
    if (!formData.date) {
      showError('La date est obligatoire');
      return;
    }
    if (formData.beneficiaire_type === 'fournisseur' && !formData.fournisseur_id) {
      showError('Veuillez sélectionner un fournisseur');
      return;
    }
    if (formData.beneficiaire_type === 'beneficiaire' && !formData.beneficiaire_id) {
      showError('Veuillez sélectionner un bénéficiaire');
      return;
    }
    if (formData.montant_total <= 0) {
      showError('Le montant doit être supérieur à 0');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        fournisseur_id: formData.beneficiaire_type === 'fournisseur' ? formData.fournisseur_id : null,
        beneficiaire_id: formData.beneficiaire_type === 'beneficiaire' ? formData.beneficiaire_id : null,
      };

      if (editId) {
        await decaissementsAPI.update(editId, submitData as DecaissementUpdate);
        showSuccess('Décaissement modifié avec succès');
      } else {
        await decaissementsAPI.create(submitData);
        showSuccess('Décaissement créé avec succès');
      }
      clearDraft();
      onSuccess();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Annuler = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ formData });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editId ? 'Modifier le Décaissement' : 'Nouveau Décaissement'}
            <DialogControls color="#9C27B0" onMinimize={minimize} onClose={handleClose} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Date and Project */}
            <Grid item xs={6}>
              <TextField
                fullWidth type="date" label="Date *" value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={projets}
                getOptionLabel={(o) => `${o.code} - ${o.nom}`}
                value={projets.find(p => p.id === formData.projet_id) || null}
                onChange={(_, v) => setFormData({ ...formData, projet_id: v?.id || null })}
                renderInput={(params) => <TextField {...params} label="Projet" />}
              />
            </Grid>

            {/* Beneficiary Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel>Type de Bénéficiaire *</FormLabel>
                <RadioGroup
                  row value={formData.beneficiaire_type}
                  onChange={(e) => setFormData({ ...formData, beneficiaire_type: e.target.value, fournisseur_id: null, beneficiaire_id: null })}
                >
                  <FormControlLabel value="fournisseur" control={<Radio />} label="Fournisseur" />
                  <FormControlLabel value="beneficiaire" control={<Radio />} label="Autre Bénéficiaire" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Beneficiary Selection */}
            <Grid item xs={12}>
              {formData.beneficiaire_type === 'fournisseur' ? (
                <Autocomplete
                  options={fournisseurs}
                  getOptionLabel={(o) => `${o.code} - ${o.name}`}
                  value={fournisseurs.find(f => f.id === formData.fournisseur_id) || null}
                  onChange={(_, v) => setFormData({ ...formData, fournisseur_id: v?.id || null })}
                  renderInput={(params) => <TextField {...params} label="Fournisseur *" />}
                />
              ) : (
                <Autocomplete
                  options={beneficiaires}
                  getOptionLabel={(o) => o.name}
                  value={beneficiaires.find(b => b.id === formData.beneficiaire_id) || null}
                  onChange={(_, v) => setFormData({ ...formData, beneficiaire_id: v?.id || null })}
                  renderInput={(params) => <TextField {...params} label="Bénéficiaire *" />}
                />
              )}
            </Grid>

            {/* Motif */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Motif *</InputLabel>
                <Select value={formData.motif} label="Motif *" onChange={(e) => setFormData({ ...formData, motif: e.target.value })}>
                  {Object.entries(MOTIF_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Pièce Justificatif" value={formData.piece_justificatif}
                onChange={(e) => setFormData({ ...formData, piece_justificatif: e.target.value })}
              />
            </Grid>

            {/* Libelle */}
            <Grid item xs={12}>
              <TextField
                fullWidth label="Libellé" multiline rows={2} value={formData.libelle}
                onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
              />
            </Grid>

            {/* Payment Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel>Type de Paiement *</FormLabel>
                <RadioGroup row value={formData.paiement_type} onChange={(e) => handlePaiementTypeChange(e.target.value)}>
                  <FormControlLabel value="totalite" control={<Radio />} label="Totalité" />
                  <FormControlLabel value="partiel" control={<Radio />} label="Partiel" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Amount Fields - Dynamic based on payment type */}
            {formData.paiement_type === 'totalite' ? (
              <Grid item xs={12}>
                <PriceInput
                  label="Montant *"
                  value={formData.montant_total || ''}
                  onChange={handleMontantTotalChange}
                  required
                  variant="outlined"
                  currency="DH"
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={4}>
                  <PriceInput
                    label="Montant Total *"
                    value={formData.montant_total || ''}
                    onChange={handleMontantTotalChange}
                    required
                    variant="outlined"
                    currency="DH"
                  />
                </Grid>
                <Grid item xs={4}>
                  <PriceInput
                    label="Montant à Payer *"
                    value={formData.montant_paye || ''}
                    onChange={handleMontantPayeChange}
                    required
                    variant="outlined"
                    currency="DH"
                  />
                </Grid>
                <Grid item xs={4}>
                  <PriceInput
                    label="Reste"
                    value={formData.reste || 0}
                    onChange={() => {}}
                    disabled
                    variant="outlined"
                    currency="DH"
                  />
                </Grid>
              </>
            )}

            {/* Payment Method */}
            <Grid item xs={6}>
              <Autocomplete
                options={modesPaiement}
                getOptionLabel={(o) => o.nom}
                value={modesPaiement.find(m => m.id === formData.mode_paiement_id) || null}
                onChange={(_, v) => setFormData({ ...formData, mode_paiement_id: v?.id || null })}
                renderInput={(params) => <TextField {...params} label="Mode de Paiement" />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="Référence Paiement" value={formData.reference_paiement}
                onChange={(e) => setFormData({ ...formData, reference_paiement: e.target.value })}
                placeholder="N° chèque, virement, etc."
              />
            </Grid>

            {/* Date d'échéance */}
            <Grid item xs={6}>
              <TextField
                fullWidth type="date" label="Date d'échéance" value={formData.date_echeance || ''}
                onChange={(e) => setFormData({ ...formData, date_echeance: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select value={formData.status} label="Statut" onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <MenuItem value="brouillon">Brouillon</MenuItem>
                  <MenuItem value="validee">Validée</MenuItem>
                  <MenuItem value="annulee">Annulée</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editId ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
      <MinimizedBar
        open={open && minimized}
        color="#9C27B0"
        title={editId ? 'Modifier le Décaissement' : 'Nouveau Décaissement'}
        onRestore={restore}
        onClose={handleClose}
      />
      <AlertDialog {...alertState} onClose={hideAlert} onConfirm={hideAlert} />
    </>
  );
};

export default DecaissementFormDialog;
